/**
 * Hook pour gérer le polling du statut de paiement Mobile Money
 * Gère l'état de l'UI pendant que l'utilisateur confirme sur son téléphone
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import subscriptionApiService, { 
  PaymentStatusResponse, 
  MobileMoneyPendingResponse 
} from '../services/subscriptionApi';
import { Subscription } from '../types/subscription';
import { useToast } from './useToast';

export type MobileMoneyStatus = 
  | 'idle'           // Pas de paiement en cours
  | 'initiating'     // Envoi de la requête initiale
  | 'pending'        // En attente de confirmation utilisateur
  | 'processing'     // Traitement en cours côté opérateur
  | 'completed'      // Paiement réussi
  | 'failed'         // Paiement échoué
  | 'expired'        // Timeout - l'utilisateur n'a pas confirmé à temps
  | 'cancelled';     // Annulé par l'utilisateur

export interface MobileMoneyState {
  status: MobileMoneyStatus;
  transactionId: string | null;
  message: string;
  provider: string | null;
  phoneNumber: string | null;
  elapsedTime: number;          // Temps écoulé en secondes
  remainingTime: number;        // Temps restant en secondes
  subscription: Subscription | null;
  error: string | null;
}

export interface UseMobileMoneyPaymentReturn {
  state: MobileMoneyState;
  startPayment: (pendingResponse: MobileMoneyPendingResponse) => Promise<Subscription>;
  cancelPolling: () => void;
  resetState: () => void;
  isPolling: boolean;
}

const INITIAL_STATE: MobileMoneyState = {
  status: 'idle',
  transactionId: null,
  message: '',
  provider: null,
  phoneNumber: null,
  elapsedTime: 0,
  remainingTime: 300, // 5 minutes par défaut
  subscription: null,
  error: null
};

// Messages utilisateur par statut
const STATUS_MESSAGES: Record<MobileMoneyStatus, string> = {
  idle: '',
  initiating: 'Initialisation du paiement...',
  pending: 'Veuillez confirmer le paiement sur votre téléphone',
  processing: 'Traitement du paiement en cours...',
  completed: 'Paiement effectué avec succès !',
  failed: 'Le paiement a échoué',
  expired: 'Le délai de confirmation a expiré',
  cancelled: 'Paiement annulé'
};

export function useMobileMoneyPayment(): UseMobileMoneyPaymentReturn {
  const [state, setState] = useState<MobileMoneyState>(INITIAL_STATE);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  
  // Refs pour contrôler le polling
  const isPollingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Timer pour mettre à jour le temps écoulé/restant
  const startTimer = useCallback((maxDuration: number) => {
    startTimeRef.current = Date.now();
    const maxSeconds = maxDuration / 1000;

    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, maxSeconds - elapsed);
      
      setState(prev => ({
        ...prev,
        elapsedTime: elapsed,
        remainingTime: remaining
      }));

      // Arrêter le timer si le temps est écoulé
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
      }
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Démarre le polling pour un paiement Mobile Money en attente
   */
  const startPayment = useCallback(async (
    pendingResponse: MobileMoneyPendingResponse
  ): Promise<Subscription> => {
    const { transactionId, provider, phoneNumber, expiresAt } = pendingResponse;

    // Calculer la durée maximale basée sur expiresAt
    const expiryTime = new Date(expiresAt).getTime();
    const maxDuration = Math.max(0, expiryTime - Date.now());

    // Initialiser l'état
    isPollingRef.current = true;
    abortControllerRef.current = new AbortController();

    setState({
      status: 'pending',
      transactionId,
      message: STATUS_MESSAGES.pending,
      provider: provider || null,
      phoneNumber: phoneNumber || null,
      elapsedTime: 0,
      remainingTime: Math.floor(maxDuration / 1000),
      subscription: null,
      error: null
    });

    // Démarrer le timer d'affichage
    startTimer(maxDuration);

    // Afficher le toast d'attente
    toastRef.current.showToast?.({
      type: 'info',
      title: 'Confirmation requise',
      message: `Veuillez confirmer le paiement ${provider ? `via ${provider}` : ''} sur votre téléphone`
    });

    try {
      const result = await subscriptionApiService.pollPaymentStatus(transactionId, {
        pollInterval: 5000,
        maxDuration,
        onStatusChange: (statusResponse) => {
          // Mettre à jour l'état à chaque changement
          let newStatus: MobileMoneyStatus = 'pending';
          
          if (statusResponse.status === 'completed') {
            newStatus = 'completed';
          } else if (statusResponse.status === 'failed') {
            newStatus = 'failed';
          } else if (statusResponse.status === 'expired') {
            newStatus = 'expired';
          }

          setState(prev => ({
            ...prev,
            status: newStatus,
            message: statusResponse.message || STATUS_MESSAGES[newStatus]
          }));
        },
        onError: (error) => {
          console.warn('⚠️ Erreur polling (continue):', error.message);
        }
      });

      // Succès !
      stopTimer();
      isPollingRef.current = false;

      const subscription = result.subscription!;

      setState(prev => ({
        ...prev,
        status: 'completed',
        message: STATUS_MESSAGES.completed,
        subscription
      }));

      toastRef.current.showToast?.({
        type: 'success',
        title: 'Paiement réussi',
        message: 'Votre abonnement a été activé avec succès !'
      });

      return subscription;

    } catch (errorResult) {
      stopTimer();
      isPollingRef.current = false;

      const paymentError = errorResult as PaymentStatusResponse;
      const errorStatus: MobileMoneyStatus = paymentError.status === 'expired' ? 'expired' : 'failed';
      const errorMessage = paymentError.message || 
        paymentError.failureReason || 
        STATUS_MESSAGES[errorStatus];

      setState(prev => ({
        ...prev,
        status: errorStatus,
        message: errorMessage,
        error: errorMessage
      }));

      toastRef.current.showToast?.({
        type: 'error',
        title: errorStatus === 'expired' ? 'Délai expiré' : 'Paiement échoué',
        message: errorMessage
      });

      throw new Error(errorMessage);
    }
  }, [startTimer, stopTimer]);

  /**
   * Annule le polling en cours
   */
  const cancelPolling = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    stopTimer();
    isPollingRef.current = false;

    setState(prev => ({
      ...prev,
      status: 'cancelled',
      message: STATUS_MESSAGES.cancelled
    }));
  }, [stopTimer]);

  /**
   * Réinitialise l'état
   */
  const resetState = useCallback(() => {
    cancelPolling();
    setState(INITIAL_STATE);
  }, [cancelPolling]);

  return {
    state,
    startPayment,
    cancelPolling,
    resetState,
    isPolling: isPollingRef.current
  };
}

export default useMobileMoneyPayment;
